import React, { useEffect, useState } from 'react'
import injectSheet from 'react-jss'
import { Link, useRouteMatch } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons/faCalendarDay'
import { faCalendarAlt } from '@fortawesome/pro-regular-svg-icons/faCalendarAlt'
import { faStopwatch } from '@fortawesome/pro-regular-svg-icons/faStopwatch'
import { faStar } from '@fortawesome/pro-regular-svg-icons/faStar'
import { faHashtag } from '@fortawesome/pro-regular-svg-icons/faHashtag'
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft'
import classNames from 'classnames'
import { apiClient } from 'pobble-components/dist/api'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import { useMe } from 'pobble-components/dist/pobble-user'
import posthog from 'posthog-js'

const SidebarItem = injectSheet({
  item: {
    position: 'relative',
    '& > *': {
      pointerEvents: 'none'
    },
    '@media (max-width: 1023px)': {
      color: '#8E909A',
      textDecoration: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px'
    },
    '@media (min-width: 1024px)': {
      font: 'var(--body)',
      fontWeight: 'bold',
      padding: '16px 24px',
      borderRadius: '8px',
      display: 'flex',
      color: '#ccc',
      alignItems: 'center',
      textDecoration: 'none',
      gap: '8px',
      '&:hover': {
        background: '#262626'
      }
    }
  },
  title: {
    '@media (max-width: 1023px)': {
      display: 'none'
    }
  },
  mobileTitle: {
    font: 'var(--body---)',
    '@media (min-width: 1024px)': {
      display: 'none'
    }
  },
  active: {
    '@media (max-width: 1023px)': {
      color: 'var(--light-gray)'
    },
    '@media (min-width: 1024px)': {
      background: '#191919 !important',
      color: '#fff'
    }
  },
  icon: {
    '@media (max-width: 1023px)': {
      fontSize: '20px'
    },
    '@media (min-width: 1024px)': {
      fontSize: '14px'
    }
  },
  subItem: {
    '@media (max-width: 1023px)': {
      display: 'none'
    },
    '@media (min-width: 1024px)': {
      font: 'var(--body--)',
      color: '#fff',
      background: '#191919',
      padding: '4px 16px',
      margin: '8px 0 8px 44px',
      display: 'inline-block',
      borderRadius: '8px',
      position: 'relative',
      '&:after': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '32px',
        background: '#ccc',
        left: '-14px',
        bottom: '10px'
      },
      '&:before': {
        content: '""',
        position: 'absolute',
        height: '1px',
        width: '8px',
        background: '#ccc',
        left: '-14px',
        bottom: '10px'
      }
    }
  }
})(
  ({
    classes,
    className,
    to,
    icon,
    children,
    active,
    mobileTitle,
    subItem
  }) => {
    return (
      <li>
        <Link
          onClick={() => {
            apiClient.post('/v3/me/activated_with_product', {
              type: 'content'
            })
            posthog.capture('activate', { type: 'content' })
            posthog.capture('click on sidebar navigation on prompt', {
              button: typeof children === 'string' ? children : children[0]
            })
            window.scrollTo(0, 0)
          }}
          className={classNames(
            classes.item,
            { [classes.active]: active },
            className
          )}
          to={to}>
          {icon && <span className={classes.icon}>{icon}</span>}
          <span className={classes.title}>{children}</span>
          <span className={classes.mobileTitle}>{mobileTitle}</span>
        </Link>
        {subItem && <span className={classes.subItem}>{subItem}</span>}
      </li>
    )
  }
)

const Sidebar = injectSheet({
  sidebar: {
    '@media (max-width: 1023px)': {
      background: '#2C2D31',
      padding: '20px',
      position: 'fixed',
      bottom: '0',
      width: '100%',
      zIndex: '100',
      height: '80px'
    },
    '@media (min-width: 1024px)': {
      width: '280px',
      background: '#2C2D31',
      padding: '0 20px',
      flexShrink: '0'
    }
  },
  holder: {
    '@media (max-width: 1023px)': {
      display: 'flex',
      gap: '24px',
      justifyContent: 'center'
    },
    '@media (min-width: 1024px)': {
      padding: '32px 0',
      position: 'sticky',
      top: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }
  },
  items: {
    '@media (max-width: 1023px)': {
      listStyle: 'none',
      padding: '0',
      margin: '0',
      display: 'flex',
      gap: '32px'
    },
    '@media (min-width: 1024px)': {
      listStyle: 'none',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '0'
    }
  },
  free: {
    font: 'var(--body---)',
    color: '#000',
    background: 'var(--light-gray)',
    borderRadius: '8px',
    padding: '2px 6px',
    marginLeft: '8px',
    position: 'absolute',
    right: '12px',
    top: '18px'
  },
  divider: {
    '@media (max-width: 1023px)': {
      width: '1px',
      height: '32px',
      background: '#61636B'
    },
    '@media (min-width: 1024px)': {
      height: '1px',
      background: '#61636B'
    }
  }
})(({ classes }) => {
  const { path, params } = useRouteMatch()
  const [openedPresentation, setOpenedPresentation] = useState(null)
  const { me } = useMe()
  const { hasAccessToTL } = useSubscriptionDetails()

  const studentWithoutSub =
    me.account.role_in_life === 'student' && !hasAccessToTL

  useEffect(
    () => {
      if (params.id) {
        apiClient
          .get(`/v3/presentations/${params.id}`, {
            params: {
              include:
                'presentation.names,presentation.details,presentation.category'
            }
          })
          .then(res => {
            let type = ''
            const presentation = res.data.presentation
            if (path === '/lesson/:id') {
              type = 'saved'
            } else if (presentation.category === 'pobble365') {
              type = 'p365'
            } else if (presentation.category === 'quick_writes') {
              type = 'quickWrite'
            } else if (presentation.category === 'theme_based') {
              type = 'topics'
            }

            setOpenedPresentation({
              title: `${presentation.title.slice(0, 24)}${
                presentation.title.length > 24 ? '...' : ''
              }`,
              type
            })
          })
      } else {
        setOpenedPresentation(null)
      }
    },
    [params, path]
  )

  let style = {}

  if (path === '/prompt/calendar') {
    style = { position: 'sticky', top: 70 }
  }

  const isPromptPage = path === '/prompt' || path === '/prompt/:id'

  return (
    <div className={classes.sidebar}>
      <div className={classes.holder} style={style}>
        <ul className={classNames(classes.items, 'metrics__lesson-categories')}>
          {isPromptPage ? (
            <SidebarItem
              className="metrics__prompt_more_lessons"
              to="/prompt/calendar"
              mobileTitle="More"
              icon={<FontAwesomeIcon icon={faAngleLeft} />}>
              More lessons
            </SidebarItem>
          ) : (
            <>
              <SidebarItem
                className="metrics__prompt_today"
                to="/prompt"
                active={path === '/prompt'}
                mobileTitle="Today's"
                icon={<FontAwesomeIcon icon={faCalendarDay} />}>
                Today's Pobble 365
                {!hasAccessToTL &&
                  !studentWithoutSub && (
                    <span className={classes.free}>Free</span>
                  )}
              </SidebarItem>
              <SidebarItem
                subItem={
                  openedPresentation &&
                  openedPresentation.type === 'p365' &&
                  openedPresentation.title
                }
                className="metrics__prompt_calendar"
                active={path === '/prompt/calendar'}
                to="/prompt/calendar"
                mobileTitle="Calendar"
                icon={<FontAwesomeIcon icon={faCalendarAlt} />}>
                Pobble 365 calendar
              </SidebarItem>
              {!studentWithoutSub && (
                <>
                  <SidebarItem
                    subItem={
                      openedPresentation &&
                      openedPresentation.type === 'quickWrite' &&
                      openedPresentation.title
                    }
                    active={path === '/prompt/weekly'}
                    to="/prompt/weekly"
                    mobileTitle="Quick"
                    className="metrics__prompt_quickwrites"
                    icon={<FontAwesomeIcon icon={faStopwatch} />}>
                    Quick writes
                  </SidebarItem>
                  <SidebarItem
                    subItem={
                      openedPresentation &&
                      openedPresentation.type === 'topics' &&
                      openedPresentation.title
                    }
                    active={path.startsWith('/prompt/topics')}
                    to="/prompt/topics/289783040"
                    mobileTitle="Topics"
                    className="metrics__prompt_topics"
                    icon={<FontAwesomeIcon icon={faHashtag} />}>
                    Topic writes
                  </SidebarItem>
                  <SidebarItem
                    active={path.startsWith('/prompt/search')}
                    to="/prompt/search"
                    mobileTitle="Search"
                    className="metrics__prompt_search"
                    icon={<FontAwesomeIcon icon={faSearch} />}>
                    Search
                  </SidebarItem>
                </>
              )}
            </>
          )}
        </ul>
        <div className={classes.divider} />
        <ul className={classes.items} style={style}>
          <SidebarItem
            subItem={
              openedPresentation &&
              openedPresentation.type === 'saved' &&
              openedPresentation.title
            }
            to="/"
            active={path === '/'}
            mobileTitle="Saved"
            className="metrics__lessons-saved"
            icon={<FontAwesomeIcon icon={faStar} />}>
            My lessons
          </SidebarItem>
        </ul>
      </div>
    </div>
  )
})

export const PromptsLayout = injectSheet({
  container: {
    '@media (min-width: 1024px)': {
      flex: 1,
      minWidth: '0',
      display: 'flex'
    }
  },
  content: {
    '@media (max-width: 1023px)': {
      paddingBottom: '80px'
    },
    '@media (min-width: 1024px)': {
      flex: 1,
      minWidth: '0'
    }
  }
})(({ classes, children, showSidebar = true }) => {
  return (
    <div className={classes.container}>
      {showSidebar && <Sidebar />}
      <div className={classes.content}>{children}</div>
    </div>
  )
})
